import React, { useState } from 'react';
import { IconAccessible as Accessibility, IconArmchair as Armchair, IconArrowsUpDown as ArrowUpDown, IconBike as Bike, IconCamera as Camera, IconSmoking as Cigarette, IconCoffee as Coffee, IconCross as Cross, IconDisc as Disc3, IconDoorEnter as DoorOpen, IconBarbell as Dumbbell, IconFlag as Flag, IconWalk as Footprints, IconTarget as Goal, IconInfoCircle as Info, IconLogout as LogOut, IconGlassCocktail as Martini, IconMedal as Medal, IconMicrophone as Mic, IconDeviceDesktop as Monitor, IconMusic as Music, IconShoppingBag as ShoppingBag, IconParking as SquareParking, IconTarget as Target, IconTicket as Ticket, IconToiletPaper as Toilet, IconTrophy as Trophy, IconToolsKitchen2 as Utensils, IconBallVolleyball as Volleyball, IconRipple as Waves, type Icon as TablerIcon } from '@tabler/icons-react';
import { Choice } from './ui';

export type ObjectIconDef = { id: string; label: string; group: 'common' | 'sports'; Icon: TablerIcon };

export const OBJECT_ICONS: ObjectIconDef[] = [
    { id: 'stage', label: 'Stage', group: 'common', Icon: Mic },
    { id: 'food', label: 'Food', group: 'common', Icon: Utensils },
    { id: 'bar', label: 'Bar', group: 'common', Icon: Martini },
    { id: 'bathroom', label: 'Bathroom', group: 'common', Icon: Toilet },
    { id: 'exit', label: 'Exit', group: 'common', Icon: LogOut },
    { id: 'dance', label: 'Dance', group: 'common', Icon: Music },
    { id: 'entrance', label: 'Entrance', group: 'common', Icon: DoorOpen },
    { id: 'coffee', label: 'Coffee', group: 'common', Icon: Coffee },
    { id: 'tickets', label: 'Tickets', group: 'common', Icon: Ticket },
    { id: 'info', label: 'Info', group: 'common', Icon: Info },
    { id: 'merch', label: 'Merch', group: 'common', Icon: ShoppingBag },
    { id: 'first-aid', label: 'First aid', group: 'common', Icon: Cross },
    { id: 'accessible', label: 'Accessible', group: 'common', Icon: Accessibility },
    { id: 'elevator', label: 'Elevator', group: 'common', Icon: ArrowUpDown },
    { id: 'parking', label: 'Parking', group: 'common', Icon: SquareParking },
    { id: 'smoking', label: 'Smoking', group: 'common', Icon: Cigarette },
    { id: 'dj', label: 'DJ', group: 'common', Icon: Disc3 },
    { id: 'screen', label: 'Screen', group: 'common', Icon: Monitor },
    { id: 'photo', label: 'Photo', group: 'common', Icon: Camera },
    { id: 'field', label: 'Field', group: 'sports', Icon: Goal },
    { id: 'court', label: 'Court', group: 'sports', Icon: Volleyball },
    { id: 'trophy', label: 'Trophy', group: 'sports', Icon: Trophy },
    { id: 'gym', label: 'Gym', group: 'sports', Icon: Dumbbell },
    { id: 'track', label: 'Track', group: 'sports', Icon: Bike },
    { id: 'pool', label: 'Pool', group: 'sports', Icon: Waves },
    { id: 'target', label: 'Target', group: 'sports', Icon: Target },
    { id: 'podium', label: 'Podium', group: 'sports', Icon: Medal },
    { id: 'finish', label: 'Finish', group: 'sports', Icon: Flag },
];

const GA_ICONS: Record<string, TablerIcon> = { 'ga-standing': Footprints, 'ga-seated': Armchair };

export const iconById = (id?: string | null): TablerIcon | null =>
    !id ? null : GA_ICONS[id] ?? OBJECT_ICONS.find(i => i.id === id)?.Icon ?? null;

type IconSource = { variant?: string; icon?: string; showIcon?: boolean; gaType?: string };

export const shapeIconId = (s: IconSource): string | null => {
    if (s.showIcon === false) return null;
    if (s.variant === 'ga') return s.gaType === 'seated' ? 'ga-seated' : 'ga-standing';
    return s.icon || null;
};

export const gaCapacityText = (s: { variant?: string; capacity?: number }) =>
    s.variant === 'ga' && s.capacity ? `Capacity: ${s.capacity}` : '';

export const ShapeContent: React.FC<{
    width: number;
    height: number;
    name: string;
    iconId?: string | null;
    sub?: string;
    fontSize?: number;
    color?: string;
}> = ({ width: w, height: h, name, iconId, sub, fontSize = 14, color = '#111827' }) => {
    const Icon = iconById(iconId);
    const iconSize = Icon ? Math.max(10, Math.min(30, h * 0.34, w * 0.34)) : 0;
    const subSize = Math.max(8, Math.round(fontSize * 0.8));
    const lines = [Icon ? iconSize : 0, name ? fontSize : 0, sub ? subSize : 0];
    const gap = 6;
    const total = lines.reduce((a, v) => a + v, 0) + gap * (lines.filter(Boolean).length - 1);
    let y = h / 2 - total / 2;
    const iconY = y;
    if (Icon) y += iconSize + gap;
    const nameY = y + fontSize / 2;
    if (name) y += fontSize + gap;
    const subY = y + subSize / 2;
    return (
        <g pointerEvents="none" className="select-none">
            {Icon && <Icon x={w / 2 - iconSize / 2} y={iconY} size={iconSize} color={color} strokeWidth={1.75} opacity={0.75} />}
            {name && <text x={w / 2} y={nameY} dy=".35em" textAnchor="middle" fontSize={fontSize} fontWeight={600} fill={color}>{name}</text>}
            {sub && <text x={w / 2} y={subY} dy=".35em" textAnchor="middle" fontSize={subSize} fill={color} opacity={0.8}>{sub}</text>}
        </g>
    );
};

export const IconPicker: React.FC<{ value?: string; onChange: (id: string) => void }> = ({ value, onChange }) => {
    const [sports, setSports] = useState(() => OBJECT_ICONS.some(i => i.id === value && i.group === 'sports'));
    return (
        <div className="space-y-1.5">
            <div className="grid grid-cols-6 gap-1">
                {OBJECT_ICONS.filter(i => i.group === 'common' || sports).map(({ id, label, Icon }) => (
                    <Choice key={id} active={value === id} title={label} onClick={() => onChange(id)}>
                        <Icon className="h-4 w-4" />
                    </Choice>
                ))}
            </div>
            <button type="button" onClick={() => setSports(v => !v)} className="w-full text-center text-xs font-medium text-primary40 hover:underline dark:text-nightAccent">
                {sports ? 'Hide sports venues' : 'Sports venues...'}
            </button>
        </div>
    );
};
